import { DOCUMENT } from '@angular/common';
import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { getLocalizedImages } from '@app/app.reducer';
import { AccessibilityFontSizePercentage } from '@core/data-access/models/accessibility/accessibility.model';
import { InitializationService } from '@core/data-access/models/service.model';
import { AccessibilityActions } from '@core/data-access/store/accessibility/accessibility.actions';
import {
  selectFontSizePercentage,
  selectIsHighContrastEnabled
} from '@core/data-access/store/accessibility/accessibility.selectors';
import { injectStyleElement, setPageBackgroundImage } from '@core/utils/dom-helpers';
import { Store } from '@ngrx/store';
import highContrast from '@src/assets/a11y/high-contrast.json';
import { generateSkinCss } from '@store/customization/customization.styles';
import { combineLatest, delay, first, withLatestFrom } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class AccessibilityService implements InitializationService {
  readonly #destroyRef = inject(DestroyRef);
  readonly #document = inject(DOCUMENT);
  readonly #store = inject(Store);

  init() {
    this.initAccessibilitySettingsSubscription();
  }

  toggleHighContrast() {
    this.#store.dispatch(AccessibilityActions.toggleHighContrast());
  }

  increaseFontSize(currentFontSizePercentage: number) {
    this.#store.dispatch(
      AccessibilityActions.setFontSizePercentage(
        Math.min(AccessibilityFontSizePercentage.Max, currentFontSizePercentage + 10)
      )
    );
  }

  decreaseFontSize(currentFontSizePercentage: number) {
    this.#store.dispatch(
      AccessibilityActions.setFontSizePercentage(
        Math.max(AccessibilityFontSizePercentage.Min, currentFontSizePercentage - 10)
      )
    );
  }

  resetState() {
    this.#store.dispatch(AccessibilityActions.resetReducer());
  }

  setHighContrastTemplateAndBackgroundImage(isHighContrastEnabled: boolean, backgroundImageUrl: string) {
    const highContrastCSS = isHighContrastEnabled ? generateSkinCss(highContrast) : '';

    injectStyleElement(this.#document, highContrastCSS, 'accessibility-high-contrast-css');
    setPageBackgroundImage(this.#document, isHighContrastEnabled, backgroundImageUrl);
  }

  setFontSizePercentage(fontSizePercentage: number) {
    this.#document.documentElement.style.fontSize = `${fontSizePercentage}%`;
  }

  private initAccessibilitySettingsSubscription() {
    const waitForAllOtherCSSTemplateStylesToLoad = 500;

    return combineLatest({
      isHighContrastEnabled: this.#store.select(selectIsHighContrastEnabled),
      fontSizePercentage: this.#store.select(selectFontSizePercentage)
    })
      .pipe(
        first(
          ({ isHighContrastEnabled, fontSizePercentage }) =>
            isHighContrastEnabled || fontSizePercentage !== AccessibilityFontSizePercentage.Default
        ),
        delay(waitForAllOtherCSSTemplateStylesToLoad),
        withLatestFrom(this.#store.select(getLocalizedImages)),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe(([{ isHighContrastEnabled, fontSizePercentage }, localizedImages]) => {
        this.setHighContrastTemplateAndBackgroundImage(isHighContrastEnabled, localizedImages.background);
        this.setFontSizePercentage(fontSizePercentage);
      });
  }
}
